import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft } from 'react-icons/fa';
import { formatCurrency } from '../../utils/currency';
import { useSettings } from '../../context/SettingsContext';
import { useToast } from '../../context/ToastContext';
import { LoadingState } from '../../components/Loading';

const STATUS_BADGE = {
  pending: 'warning',
  processing: 'info',
  shipped: 'info',
  delivered: 'success',
  cancelled: 'danger',
};

const AdminOrderDetail = () => {
  const { id } = useParams();
  const { settings } = useSettings();
  const toast = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadOrder(); }, [id]);

  const loadOrder = async () => {
    try {
      const res = await axios.get(`/api/orders/${id}`);
      setOrder(res.data);
      setStatus(res.data.status);
      setTrackingNumber(res.data.trackingNumber || '');
    } catch (e) {
      toast.error('Could not load order');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/orders/${id}/status`, { status, trackingNumber });
      toast.success('Order updated');
      loadOrder();
    } catch (error) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  if (!order) return (
    <div className="empty-state">
      <h3>Order not found</h3>
      <Link to="/admin/orders" className="btn btn-primary">Back to orders</Link>
    </div>
  );

  const addr = order.shippingAddress || {};

  return (
    <div>
      <div className="admin-header">
        <div>
          <Link to="/admin/orders" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--gray-600)', fontSize: 13, marginBottom: 8 }}>
            <FaArrowLeft /> Orders
          </Link>
          <h1>{order.orderNumber}</h1>
          <p className="admin-header-subtitle">
            Placed {new Date(order.createdAt).toLocaleDateString('en-US', { dateStyle: 'long' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <span className={`badge ${STATUS_BADGE[order.status] || 'neutral'}`}>{order.status}</span>
          <span className={`badge ${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>
            Payment: {order.paymentStatus}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20, alignItems: 'start' }} className="order-admin-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header"><h2>Items</h2></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {order.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--gray-100)' }}>
                  {item.product?.images?.[0] ? (
                    <img src={item.product.images[0].url} alt={item.name} style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--gray-100)' }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      {item.quantity} × {formatCurrency(item.price, settings?.currency)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700 }}>
                    {formatCurrency(item.subtotal, settings?.currency)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2>Update order</h2></div>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tracking number</label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header"><h2>Customer</h2></div>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <strong>{order.customer?.name}</strong><br />
              <span style={{ color: 'var(--gray-500)' }}>{order.customer?.email}</span>
              {order.customer?.phone && (
                <><br /><span style={{ color: 'var(--gray-500)' }}>{order.customer.phone}</span></>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2>Summary</h2></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
              <span>Subtotal</span><span>{formatCurrency(order.subtotal, settings?.currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
              <span>Tax</span><span>{formatCurrency(order.tax || 0, settings?.currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
              <span>Shipping</span><span>{formatCurrency(order.shipping || 0, settings?.currency)}</span>
            </div>
            <div style={{ height: 1, background: 'var(--gray-100)', margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 16, fontWeight: 700 }}>
              <span>Total</span><span>{formatCurrency(order.total, settings?.currency)}</span>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2>Shipping</h2></div>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--gray-700)' }}>
              <strong>{addr.name}</strong><br />
              {addr.street}<br />
              {addr.city}, {addr.state} {addr.zipCode}<br />
              {addr.country}<br />
              {addr.phone && <>📞 {addr.phone}</>}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2>Payment</h2></div>
            <div style={{ fontSize: 14, textTransform: 'capitalize' }}>
              <strong>{order.paymentMethod}</strong>
              <div style={{ marginTop: 6 }}>
                <span className={`badge ${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>
                  {order.paymentStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .order-admin-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default AdminOrderDetail;
